<template>
<div>
    <div v-if="loading" class="columns">
        <div class="column is-full has-text-centered">
            <img src="/loader-dark.svg">
        </div>
    </div>

    <div v-if="!loading" class="default-settings-title">
        <span class="account-name">Step 3: Final details</span>
        <span class="default-settings-subtitle">Now it's time to select which files should be included in the backup.</span>
    </div>

    <div v-if="!loading" class="columns is-multiline">
        <div v-if="error" class="column is-full">
            <div class="notification is-danger" style="font-size: 13px; margin-top: 20px;">{{ error }}</div>
        </div>

        <div class="column is-full m-top-20">
            <div class="field">
                <input type="text" v-model="name" @keyup.enter="createFile" :class="{ 'is-danger': hasError('name') }" class="input custom-input-size is-medium" placeholder="Schedule name" autocomplete="off" :disabled="false" />
                <span v-if="!hasError('name')" class="help">An intuitive name to recognize the file backup schedule later, e.g. <b>my website backup</b></span>
                <span v-if="hasError('name')" class="help is-danger">{{ getError('name') }}</span>
            </div>
        </div>

        <div class="column is-full">
            <div style="color: #353b48; margin-bottom: 8px; margin-top: 10px;">File paths to backup</div>
            
            <div class="columns is-multiline">
                <div class="column is-12">
                    <div class="field">
                        <textarea v-model="includedPaths" class="textarea custom-input-size" :class="{ 'is-danger': hasError('included_paths') }" placeholder="/var/www/html/my-website" style="padding: 10px 15px !important;"></textarea>
                        <span v-if="!hasError('included_paths')" class="help">Absolute paths of directories/files that should be included in the backup. One path per line.</span>
                        <span v-if="hasError('included_paths')" class="help is-danger">{{ getError('included_paths') }}</span>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="column is-full">
            <div style="color: #353b48; margin-bottom: 8px; margin-top: 10px;">File paths to exclude</div>
            
            <div class="columns is-multiline">
                <div class="column is-12">
                    <div class="field">
                        <textarea v-model="excludedPaths" class="textarea custom-input-size" :class="{ 'is-danger': hasError('excluded_paths') }" style="padding: 10px 15px !important;"></textarea>
                        <span v-if="!hasError('excluded_paths')" class="help">Absolute paths of directories/files that should be excluded from the backup. One path per line.</span>
                        <span v-if="hasError('excluded_paths')" class="help is-danger">{{ getError('excluded_paths') }}</span>

                        <!-- <input type="text" :class="{ 'is-danger': hasError('host') }" class="input custom-input-size is-medium" placeholder="/" autocomplete="off" :disabled="storingServer" /> -->
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="!loading" class="columns is-multiline">
        <div class="column is-3">
            <div @click="createFile" :disabled="creatingFile" :class="{ 'is-loading': creatingFile }" class="button custom-button-size is-fullwidth is-info tooltip" data-tooltip="This will not run the backup now.">Create</div>
        </div>
        <div class="column is-3">
            <div @click="close" class="close-add-monitor-modal button custom-button-size is-fullwidth is-danger" :disabled="creatingFile">Cancel</div>
        </div>
    </div>
</div>
</template>

<script>
import Api from '@/api';
import FormErrorsMixin from '@/mixins/FormErrors';
import swal from 'sweetalert';

export default {    
    props: ['server'],

    mixins: [FormErrorsMixin],

    data() {
        return {
            loading: true,
            checkingSshConnection: false,
            error: '',
            //
            allowedInputs: ['name', 'included_paths', 'excluded_paths', 'server_id'],
            name: '',
            creatingFile: false,
            includedPaths: '',
            excludedPaths:
`/tmp
/backups
/boot
/run
/proc
/sys
/mnt
/media
/dev
/var/run
/var/cache
/var/tmp
/var/lib/apt/lists
/var/lib/lxcfs/
/bin
/lib
/lib64
/sbin
/usr/share
/usr/src
/usr/lib
/usr/bin
/usr/sbin
/swap
/swapfile`
        }
    },

    mounted() {
        this.initialSshConnectionCheck();
    },

    methods: {
        createFile() {
            if (this.creatingFile) {
                return;
            }

            this.creatingFile = true;

            Api().post('files', this.newFileData)
                .then((success) => {
                    this.$router.push({ name: 'showFile', params: { id: success.data.data.id}, query: { file_created: true, }});
                })
                .catch((error) => {
                    this.creatingFile = false;
                    this.storeErrors(error.response.data.errors);                 
                });
        },

        initialSshConnectionCheck() {
            this.loading = true;
            let endpoint = 'servers/' + this.server.id + '/ssh-connection';

            Api().get(endpoint)
                .then((success) => {
                    if (success.data.has_working_ssh_connection) {
                        this.moveToNextStep();
                    }
                })
                .catch((error) => {
                    this.error = 'Something went wrong. Our team was informed about the error and will have a look as soon as possible. In the meantime, you can reach us out at hi@weap.io';
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        checkSshConnection() {
            if (this.checkingSshConnection) {
                return;
            }

            this.checkingSshConnection = true;
            let endpoint = 'servers/' + this.server.id + '/ssh-connection';

            Api().get(endpoint)
                .then((success) => {
                    if (success.data.has_working_ssh_connection) {
                        this.moveToNextStep();
                    } else {
                        this.error = 'The weap.io Bot is not able to connect to the server. Please make sure the given public SSH key was added to the authorized_keys file.';
                    }
                })
                .catch((error) => {
                    this.error = 'Something went wrong. Our team was informed about the error and will have a look as soon as possible. In the meantime, you can reach us out at hi@weap.io';
                })
                .finally(() => {
                    this.checkingSshConnection = false;
                });
        },

        moveToNextStep() {
            Event.$emit('moveToFileDetailsStep', this.server);
        },

        close() {
            if (this.checkingSshConnection) {
                return;
            }

            Event.$emit('closeAddFile');
        },

        nextButtonIsDisabled() {
            return this.creatingFile;
        },

        cancelButtonIsDisabled() {
            return this.creatingFile;
        },
    },

    computed: {
        newFileData() {
            return {
                name: this.name,
                included_paths: this.includedPaths,
                excluded_paths: this.excludedPaths,
                server_id: this.server.id,
            }
        },
    },
}
</script>